/**
 * System Settings Hooks
 *
 * React Query hooks for system settings management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { validatedApi } from '../../../lib/api/validated-client';
import type {
  GroupedSettingsResponse,
  SystemSettingWithMetadata,
  BulkUpdateSettingsRequest,
  PublicPricingRates,
} from '@lov/shared';

const SETTINGS_KEY = ['settings'];
const PRICING_RATES_KEY = ['settings', 'pricing-rates'];

/**
 * Fetch all settings grouped by category
 */
export function useSettings() {
  return useQuery({
    queryKey: [...SETTINGS_KEY, 'grouped'],
    queryFn: async (): Promise<GroupedSettingsResponse> => {
      return validatedApi.get('/settings/grouped');
      
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Update a single setting
 */
export function useUpdateSetting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ key, value, type }: { key: string; value: string; type?: string }) => {
      return validatedApi.put(`/settings/${key}`, { value, type });
      
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
    },
  });
}

/**
 * Bulk update multiple settings
 */
export function useBulkUpdateSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: BulkUpdateSettingsRequest) => {
      return validatedApi.put('/settings', request);
      
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
    },
  });
}

/**
 * Initialize default settings
 */
export function useInitializeSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return validatedApi.post('/settings/initialize');
      
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
    },
  });
}

/**
 * Clear settings cache
 */
export function useClearSettingsCache() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return validatedApi.post('/settings/cache/clear');

    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
    },
  });
}

/**
 * Fetch public pricing rates (no auth required)
 * Used for client-side price calculation and dynamic tooltips
 */
export function usePricingRates() {
  return useQuery({
    queryKey: PRICING_RATES_KEY,
    queryFn: async (): Promise<PublicPricingRates> => {
      return validatedApi.get('/settings/public/pricing-rates');
    },
    staleTime: 1000 * 60 * 5, // 5 minutes cache
  });
}

export type { GroupedSettingsResponse, SystemSettingWithMetadata };
